import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./Contact.css";
import '@fortawesome/fontawesome-free/css/all.min.css';
import base_url from "../../api";

const Contact = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [hasPrivacyConsent, setHasPrivacyConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState({ type: "", text: "" });

  // Fetch user data if logged in
  useEffect(() => {
    const fetchUserData = async () => {
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const response = await fetch(`${base_url}/me`, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          });

          if (response.ok) {
            const data = await response.json();
            if (data.user) {
              setUser(data.user);
              setFormData({
                name: `${data.user.firstName} ${data.user.lastName}`,
                email: data.user.email,
                phone: data.user.phone || "",
                message: "",
              });
            }
          } else {
            setUser(null);
            localStorage.removeItem("token");
            localStorage.removeItem("user");
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
          setUser(null);
        }
      } else {
        setUser(null);
      }
    };

    fetchUserData();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (user && name !== "message") {
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      setSubmitMessage({
        type: "error",
        text: "Please login first to send a message.",
      });
      setTimeout(() => {
        navigate("/login");
      }, 2000);
      return;
    }

    if (!hasPrivacyConsent) {
      setSubmitMessage({
        type: "error",
        text: "Please consent to the Privacy Policy before submitting.",
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage({ type: "", text: "" });

    try {
      const response = await fetch(`${base_url}/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...formData,
          consentGiven: hasPrivacyConsent,
          consentTimestamp: new Date().toISOString(),
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitMessage({
          type: "success",
          text: data.message || "Your message has been sent successfully!",
        });
        setFormData((prev) => ({
          ...prev,
          message: "",
        }));
        setTimeout(() => {
          setSubmitMessage({ type: "", text: "" });
        }, 5000);
      } else {
        setSubmitMessage({
          type: "error",
          text: data.message || "Failed to send message. Please try again.",
        });
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setSubmitMessage({
        type: "error",
        text: "Failed to send message. Please try again later.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-container">
      {/* Hero Section */}
      <section className="page-hero">
        <div className="page-hero-content">
          <div className="operator-notice">
            This page is operated by{" "}
            <a href="https://www.rxconnect.ca/" target="_blank" rel="noopener noreferrer">
              Rx Connect Specialty Pharmacy
            </a>
            . All services are provided through the licensed pharmacy.
          </div>
          <h1>Get in Touch</h1>
          <p className="page-hero-subtitle">
            Whether you're ready to start your case or just need advice, we're here to help.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="page-content">
        <div className="page-wrapper">
          {/* Contact Info Section */}
          <div className="contact-info-section">
            <h2>Contact Information</h2>
            <p className="contact-intro">
              We're here to help you navigate your family law matters. Reach out through any of the methods below, 
              or fill out the contact form and we'll get back to you within 24 hours.
            </p>

            <div className="contact-info-grid">
              <div className="info-card-large">
                <div className="info-icon-large"><i className="fa-solid fa-phone"></i></div>
                <h3>Phone</h3>
                <p className="info-link">01900 123456</p>
                <p className="info-note">Monday - Friday, 9am - 5pm</p>
              </div>

              <div className="info-card-large">
                <div className="info-icon-large"><i className="fa-solid fa-envelope"></i></div>
                <h3>Email</h3>
                <p className="info-link">info@sandyrandhawa.com</p>
                <p className="info-note">We typically respond within 24 hours</p>
              </div>

              <div className="info-card-large">
                <div className="info-icon-large"><i className="fa-solid fa-location-dot"></i></div>
                <h3>Office Address</h3>
                <p>
                  123 Legal Street<br />
                  Manchester, England<br />
                  M1 1AA
                </p>
                <p className="info-note">By appointment only</p>
              </div>
            </div>
          </div>

          {/* Contact Form Section */}
          <div className="contact-form-section">
            <h2>Send Us a Message</h2>
            <p className="form-intro">
              Fill out the form below and we'll get back to you as soon as possible. 
              All consultations are confidential.
            </p>

            {!user && (
              <div className="login-prompt">
                <p>Please <a href="login">login</a> first to send a message.</p>
              </div>
            )}

            <form className="contact-form-full" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={!user ? "Please login first" : formData.name}
                    placeholder={!user ? "Please login first" : "Your full name"}
                    disabled={true}
                    required
                    className="form-input-disabled"
                  />
                </div>

                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={!user ? "Please login first" : formData.email}
                    placeholder={!user ? "Please login first" : "your@email.com"}
                    disabled={true}
                    required
                    className="form-input-disabled"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={!user ? "Please login first" : formData.phone}
                  placeholder={!user ? "Please login first" : "Your phone number"}
                  disabled={true}
                  className="form-input-disabled"
                />
              </div>

              <div className="form-group">
                <label>Message *</label>
                <textarea
                  name="message"
                  value={!user ? "Please login first" : formData.message}
                  onChange={handleInputChange}
                  rows="6"
                  placeholder={!user ? "Please login first" : "Tell us about your situation and how we can help..."}
                  disabled={!user}
                  required
                  className={!user ? "form-input-disabled" : ""}
                />
              </div>

              <div className="consent-row">
                <input
                  id="privacy-consent"
                  type="checkbox"
                  checked={hasPrivacyConsent}
                  onChange={(e) => setHasPrivacyConsent(e.target.checked)}
                  disabled={!user}
                  required
                />
                <label htmlFor="privacy-consent">
                  I consent to the collection and use of my personal
                  information in accordance with the{" "}
                  <Link to="/privacy-policy">Privacy Policy</Link>.
                </label>
              </div>

              {submitMessage.text && (
                <div className={`form-message ${submitMessage.type}`}>
                  {submitMessage.text}
                </div>
              )}

              <button
                type="submit"
                className="submit-button"
                disabled={isSubmitting || !user || !hasPrivacyConsent}
              >
                {!user ? "Please Login First" : isSubmitting ? "Sending..." : "Send Message"}
              </button>

              <p className="jit-privacy-notice">
                We collect your information to respond to your enquiry and may
                share it with{" "}
                <a href="https://www.rxconnect.ca/" target="_blank" rel="noopener noreferrer">
                  Rx Connect Specialty Pharmacy
                </a>{" "}
                and relevant healthcare providers. Your data will be handled
                securely and in accordance with our{" "}
                <Link to="/privacy-policy">Privacy Policy</Link>.
              </p>

              <p className="form-note">
                All consultations are confidential. We typically respond within
                24 hours. Read our <Link to="/privacy-policy">Privacy Policy</Link>.
              </p>
            </form>
          </div>

          {/* Additional Info */}
          <div className="contact-additional">
            <h2>What Happens Next?</h2>
            <div className="next-steps">
              <div className="step-box">
                <div className="step-number">1</div>
                <h4>We Review Your Message</h4>
                <p>Our team reviews your inquiry and prepares a response tailored to your situation.</p>
              </div>
              <div className="step-box">
                <div className="step-number">2</div>
                <h4>We Get in Touch</h4>
                <p>We'll contact you within 24 hours to discuss your case and answer any questions.</p>
              </div>
              <div className="step-box">
                <div className="step-number">3</div>
                <h4>Free Consultation</h4>
                <p>We'll schedule a free initial consultation to discuss your case in detail.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
